import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, User, X, Phone } from 'lucide-react';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { createUserDocument, getUserData } from './utils/userService';
import { useLandingPageCleanup } from './contexts/LandingPageContext';

const LoginPage = ({ modalMode = false, onSuccess, signupSource = 'landing' }) => {
  const navigate = useNavigate();
  const { isCleaningUp, cleanupComplete } = useLandingPageCleanup();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [premiumMessage, setPremiumMessage] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Eğer kullanıcı zaten giriş yapmışsa profil sayfasına yönlendir (modal mode'da değilse)
  useEffect(() => {
    if (modalMode) return; // Modal mode'da navigate etme
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/profile');
      }
    });
    return () => unsubscribe();
  }, [navigate, modalMode]);

  const getAuthErrorMessage = (code, isLogin) => {
    const messages = {
      'auth/invalid-email': 'Geçerli bir e-posta adresi giriniz.',
      'auth/user-disabled': 'Hesap devre dışı bırakılmış.',
      'auth/user-not-found': 'Bu e-posta ile kayıtlı kullanıcı bulunamadı.',
      'auth/wrong-password': 'E-posta veya şifre hatalı.',
      'auth/invalid-credential': 'E-posta veya şifre hatalı. Lütfen bilgilerinizi kontrol ediniz.',
      'auth/email-already-in-use': 'Bu e-posta adresiyle kayıtlı bir hesap zaten var.',
      'auth/weak-password': 'Şifre en az 6 karakter olmalıdır.',
      'auth/too-many-requests': 'Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyiniz.'
    };

    if (messages[code]) {
      return messages[code];
    }

    return isLogin
      ? 'Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyiniz.'
      : 'Kayıt olurken bir hata oluştu. Lütfen tekrar deneyiniz.';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isLoginMode) {
      if (!firstName.trim()) {
        newErrors.firstName = 'İsim gereklidir';
      }
    }

    if (!email.trim()) {
      newErrors.email = 'E-posta adresi gereklidir';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!password) {
      newErrors.password = 'Şifre gereklidir';
    } else if (password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    if (!isLoginMode) {
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Şifre tekrar gereklidir';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Şifreler eşleşmiyor';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setErrors({});
    setPremiumMessage('');
    
    try {
      // Auth'un hazır olduğundan emin ol
      if (!auth) {
        throw new Error('Firebase Auth henüz hazır değil. Lütfen sayfayı yenileyin.');
      }

      const result = await signInWithPopup(auth, googleProvider);
      // Google ile giriş başarılı (kayıtlı değilse otomatik kayıt olur)
      // Firestore'da user document oluştur (yoksa)
      await createUserDocument(
        result.user.uid,
        result.user.email || '',
        result.user.displayName || '',
        '', // phoneNumber
        signupSource
      );
      
      // Kullanıcı verilerini kontrol et
      const userData = await getUserData(result.user.uid);
      
      if (modalMode && onSuccess) {
        // Modal mode'da onSuccess callback'i çağır
        onSuccess();
      } else {
      if (userData && userData.isPremium) {
        // Premium kullanıcı, video sayfasına yönlendir
        navigate('/lectures');
      } else {
        // Premium değil, direkt ana sayfaya yönlendir
        navigate('/');
        }
      }
    } catch (error) {
      console.error('Google sign in error:', error.code, error.message);
      
      // Spesifik hata mesajları
      let errorMessage = 'Google ile giriş yapılırken bir hata oluştu. Lütfen tekrar deneyiniz.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Giriş penceresi kapatıldı. Lütfen tekrar deneyiniz.';
      } else       if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup engellendi. Lütfen tarayıcı ayarlarınızdan popup izni verin ve tekrar deneyiniz.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Giriş işlemi iptal edildi. Lütfen tekrar deneyiniz.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Ağ hatası. İnternet bağlantınızı kontrol ediniz.';
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      if (isLoginMode) {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        
        // Kullanıcı verilerini kontrol et
        const userData = await getUserData(credential.user.uid);
        
        if (modalMode && onSuccess) {
          // Modal mode'da onSuccess callback'i çağır
          onSuccess();
        } else {
        if (userData && userData.isPremium) {
          // Premium kullanıcı, video sayfasına yönlendir
          navigate('/lectures');
        } else {
          // Premium değil, direkt ana sayfaya yönlendir
          navigate('/');
          }
        }
      } else {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        if (firstName.trim()) {
          await updateProfile(credential.user, { displayName: firstName.trim() });
        }
        // Firestore'da user document oluştur
        await createUserDocument(
          credential.user.uid,
          email,
          firstName.trim(),
          phoneNumber.trim() || '', // Optional
          signupSource
        );
        console.log('Register successful:', credential.user);
        if (modalMode && onSuccess) {
          // Modal mode'da onSuccess callback'i çağır
          onSuccess();
        } else {
        navigate('/lectures');
        }
      }
    } catch (error) {
      setErrors({ general: getAuthErrorMessage(error.code, isLoginMode) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowForgotPasswordModal(true);
    setForgotPasswordEmail('');
    setForgotPasswordSuccess(false);
    setForgotPasswordError('');
  };

  const handleCloseForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail('');
    setForgotPasswordSuccess(false);
    setForgotPasswordError('');
  };

  const handleSendPasswordReset = async () => {
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);

    try {
      if (!auth) {
        throw new Error('Firebase Auth henüz hazır değil. Lütfen sayfayı yenileyin.');
      }

      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordSuccess(true);
      setForgotPasswordError('');
    } catch (error) {
      setForgotPasswordError(getAuthErrorMessage(error.code, true));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // CSS cleanup tamamlanana kadar loading göster
  if (isCleaningUp || !cleanupComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-white text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Başlık Alanı */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isLoginMode ? 'Hoş Geldiniz' : 'Kayıt Ol'}
          </h1>
          <p className="text-gray-400">
            {isLoginMode ? 'Hesabınıza giriş yapın' : 'Yeni hesap oluşturun'}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 p-8">
          {errors.general && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {errors.general}
            </div>
          )}
          
          {premiumMessage && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
              {premiumMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name Input - Sadece kayıt modunda */}
            {!isLoginMode && (
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  İsim <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) setErrors({ ...errors, firstName: '' });
                    }}
                    className={`w-full pl-12 pr-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.firstName
                        ? 'border-red-500 focus:ring-red-500/50'
                        : 'border-gray-600 focus:ring-purple-500/50 focus:border-purple-500'
                    }`}
                    placeholder="Ad-Soyad"
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-400">{errors.firstName}</p>
                )}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                E-posta Adresi {!isLoginMode && <span className="text-red-400">*</span>}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: '' });
                  }}
                  className={`w-full pl-12 pr-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.email
                      ? 'border-red-500 focus:ring-red-500/50'
                      : 'border-gray-600 focus:ring-purple-500/50 focus:border-purple-500'
                  }`}
                  placeholder="ornek@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Şifre {!isLoginMode && <span className="text-red-400">*</span>}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: '' });
                  }}
                  className={`w-full pl-12 pr-12 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.password
                      ? 'border-red-500 focus:ring-red-500/50'
                      : 'border-gray-600 focus:ring-purple-500/50 focus:border-purple-500'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-400">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Input - Sadece kayıt modunda */}
            {!isLoginMode && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Şifre Tekrar <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                    }}
                    className={`w-full pl-12 pr-12 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.confirmPassword
                        ? 'border-red-500 focus:ring-red-500/50'
                        : 'border-gray-600 focus:ring-purple-500/50 focus:border-purple-500'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            )}

            {/* Telefon Numarası Input - Sadece kayıt modunda */}
            {!isLoginMode && (
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-300 mb-2">
                  Telefon Numarası <span className="text-gray-500 text-xs">(Opsiyonel)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="+90 555 123 45 67"
                  />
                </div>
              </div>
            )}

            {/* Forgot Password - Sadece login modunda */}
            {isLoginMode && (
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Şifremi unuttum
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                isLoading
                  ? 'bg-gray-700 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 transform hover:scale-[1.02]'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isLoginMode ? 'Giriş yapılıyor...' : 'Kayıt yapılıyor...'}
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <LogIn className="w-5 h-5 mr-2" />
                  {isLoginMode ? 'Giriş Yap' : 'Kayıt Ol'}
                </span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-800 text-gray-400">veya</span>
              </div>
            </div>
          </div>

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
              isLoading
                ? 'bg-gray-700 text-white cursor-not-allowed'
                : 'bg-white text-gray-900 hover:bg-gray-100 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
            }`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Google ile Giriş Yap</span>
          </button>

          {/* Toggle Login/Register Link */}
          <div className="text-center mt-6">
            <p className="text-base text-gray-400">
              {isLoginMode ? (
                <>
                  Hesabınız yok mu?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(false);
                      setErrors({});
                    }}
                    className="font-semibold text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Kayıt olun
                  </button>
                </>
              ) : (
                <>
                  Zaten hesabınız var mı?{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(true);
                      setErrors({});
                      setFirstName('');
                      setConfirmPassword('');
                    }}
                    className="font-semibold text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Giriş yapın
                  </button>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>© 2025 Tüm hakları saklıdır.</p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleCloseForgotPasswordModal}
        >
          <div 
            className="bg-gray-800/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700/50 w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseForgotPasswordModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Modal Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Şifremi Unuttum
              </h2>
              <p className="text-gray-400 text-sm">
                Şifre sıfırlama bağlantısı mail adresinize gönderilecektir. Lütfen SPAM klasörünüzü de kontrol ediniz.
              </p>
            </div>

            {/* Success Message */}
            {forgotPasswordSuccess && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                <p className="font-semibold mb-1">E-posta gönderildi!</p>
                <p>
                  Eğer bu e-posta adresi kayıtlıysa, şifre sıfırlama bağlantısı gönderildi. 
                  Lütfen e-postanızı kontrol edin.
                </p>
              </div>
            )}

            {/* Error Message */}
            {forgotPasswordError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                {forgotPasswordError}
              </div>
            )}

            {/* Email Input */}
            {!forgotPasswordSuccess && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-300 mb-2">
                    E-posta Adresi
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      id="forgotPasswordEmail"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => {
                        setForgotPasswordEmail(e.target.value);
                        setForgotPasswordError('');
                      }}
                      className="w-full pl-12 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                      placeholder="ornek@email.com"
                      disabled={forgotPasswordLoading}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseForgotPasswordModal}
                    disabled={forgotPasswordLoading}
                    className="flex-1 py-3 px-4 rounded-lg font-semibold text-white bg-gray-700 hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    İptal
                  </button>
                  <button
                    type="button"
                    onClick={handleSendPasswordReset}
                    disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${
                      forgotPasswordLoading || !forgotPasswordEmail.trim()
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 transform hover:scale-[1.02]'
                    }`}
                  >
                    {forgotPasswordLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Gönderiliyor...
                      </span>
                    ) : (
                      'Gönder'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Success State - Close Button */}
            {forgotPasswordSuccess && (
              <button
                onClick={handleCloseForgotPasswordModal}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 transition-all duration-200"
              >
                Tamam
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;

